import { useId, type ReactNode } from "react";
import { Button } from "./Button.js";
import { Inline } from "./Inline.js";
import { Modal } from "./Modal.js";
import { Stack } from "./Stack.js";

/**
 * GT-FLEET-CUSTOMER-READINESS-SHARED-UX-VEHICLES-DRIVERS-01 — the
 * shared confirmation composition for **reversible** lifecycle actions
 * (Archive, Restore, and equivalents).
 *
 * It replaces `window.confirm()`, which the FLEET module used for
 * vehicle archive and device archive/restore. A native confirm cannot
 * be themed, cannot be translated in layout, ignores RTL, is styled by
 * the browser rather than the design system, and gives the operator no
 * context beyond a single sentence.
 *
 * Irreversible actions do NOT use this composition — they use
 * `DeleteWithConfirm`, which keeps the type-the-identifier gate. Both
 * sit on the same `Modal` foundation so the focus, Escape, scrim and
 * focus-return behaviour is identical; only the friction differs.
 */
export interface ConfirmDialogProps {
  readonly open: boolean;
  /** Dialog heading — also the accessible name. */
  readonly title: string;
  /** Explanatory body. Wired to `aria-describedby`. */
  readonly body: ReactNode;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  /**
   * `danger` paints the confirm button with the danger fill. Reserved
   * for actions with real consequences; Archive/Restore use `default`.
   */
  readonly tone?: "default" | "danger";
  /** True while `onConfirm` is in flight — blocks dismissal and double submits. */
  readonly pending?: boolean;
  /** Localised failure message rendered inside the dialog. */
  readonly errorMessage?: string | null;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  /**
   * Anchors `-dialog`, `-confirm`, `-cancel`, `-error` test ids so call
   * sites can pin assertions.
   */
  readonly testIdPrefix?: string;
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  tone = "default",
  pending = false,
  errorMessage = null,
  onConfirm,
  onCancel,
  testIdPrefix,
}: ConfirmDialogProps) {
  const bodyId = useId();
  const id = (suffix: string) => (testIdPrefix ? `${testIdPrefix}-${suffix}` : undefined);

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      describedBy={bodyId}
      dismissible={!pending}
      {...(id("dialog") ? { dataTestId: id("dialog")! } : {})}
    >
      <Stack gap={4}>
        <span
          id={bodyId}
          style={{ color: "var(--gt-sys-text-muted)", fontSize: "var(--gt-sys-font-size-sm)" }}
        >
          {body}
        </span>

        {errorMessage ? (
          <span
            role="alert"
            data-testid={id("error")}
            style={{ color: "var(--gt-sys-danger)", fontSize: "var(--gt-sys-font-size-sm)" }}
          >
            {errorMessage}
          </span>
        ) : null}

        <Inline gap={2} justify="end" wrap>
          <Button
            variant="secondary"
            type="button"
            onClick={onCancel}
            disabled={pending}
            data-testid={id("cancel")}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={tone === "danger" ? "danger" : "primary"}
            type="button"
            onClick={onConfirm}
            loading={pending}
            data-testid={id("confirm")}
          >
            {confirmLabel}
          </Button>
        </Inline>
      </Stack>
    </Modal>
  );
}
